// Names and messages for what a product save did at each Publish To
// destination (upsertProduct's sync_results).

export const DESTINATION_NAMES: Record<string, string> = {
  zoho: 'Zoho',
  b2b: 'B2B Woo',
  web: 'Web Woo',
  n8n: 'Faire',
}

export const DESTINATION_ORDER = ['zoho', 'b2b', 'web', 'n8n']

export type SyncProblem = { destination: string; status: string; message: string }

export function destinationName(value: string): string {
  return DESTINATION_NAMES[value] ?? value
}

export function listNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? ''
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
}

/** Reads upsertProduct's sync_results into what went through and what didn't. */
export function readSyncResults(raw: unknown): { reached: string[]; problems: SyncProblem[] } {
  const reached: string[] = []
  const problems: SyncProblem[] = []
  if (raw && typeof raw === 'object') {
    for (const dest of DESTINATION_ORDER) {
      const r = (raw as Record<string, { status?: string; message?: string } | undefined>)[dest]
      if (!r?.status) continue
      if (r.status === 'ok') reached.push(dest)
      else problems.push({ destination: dest, status: r.status, message: r.message ?? '' })
    }
  }
  return { reached, problems }
}

/** One sentence per destination that failed or was skipped, without repeats. */
export function describeProblems(problems: SyncProblem[]): string {
  const seen = new Set<string>()
  const lines: string[] = []
  for (const p of problems) {
    const text =
      p.status === 'skipped'
        ? `${destinationName(p.destination)} skipped: ${p.message}.`
        : `${destinationName(p.destination)} failed${p.message ? `: ${p.message}` : ''}.`
    if (seen.has(text)) continue
    seen.add(text)
    lines.push(text)
  }
  return lines.join(' ')
}

/**
 * After an order is marked shipped or delivered: the Zoho / web store updates
 * that failed (markOrderFulfilled and closeOrderAsShipped's sync_results), as
 * one sentence, or '' when nothing failed. Skips are normal (already done).
 */
export function fulfillmentSyncWarning(raw: unknown, orderNumber?: string): string {
  const failed = readSyncResults(raw).problems.filter(p => p.status === 'failed')
  if (failed.length === 0) return ''
  const prefix = orderNumber ? `${orderNumber}: ` : ''
  return `${prefix}${describeProblems(failed)} The order is still marked in the Hub.`
}
